import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { IMotorPolicy } from '@inshuwa-client/models';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import { DocumentService, IDocumentDownload } from 'libs/core/src/lib/services/document.service';
import { ICDateFormats } from 'libs/core/src/lib/utils/date-utils';
import { isPiczInsurer } from 'libs/core/src/lib/utils/insurer.utils';
import { PaymentsService } from 'libs/core/src/lib/services/payments.service';
import { PoliciesService } from 'libs/core/src/lib/services/policies.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'ic-payment-success-page',
  template: `
    @if (useNewUi) {
      <div class="flex min-h-screen flex-col">
        <main class="flex-1 bg-gradient-to-b from-white to-gray-50">
          <div class="container px-4 py-6 md:px-6 md:py-10">
            <div class="mx-auto max-w-3xl text-center mb-6 md:mb-12">
              <div
                class="inline-flex items-center justify-center p-3 md:p-4 bg-primary/10 rounded-full mb-3 md:mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-green-500 size-12"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
              </div>
              <h1 class="text-2xl font-bold md:text-4xl mb-2 md:mb-3">
                Purchase Successful!
              </h1>
              <p class="text-sm text-gray-500 md:text-xl">
                Your motor insurance policy has been successfully purchased and
                is now active.
              </p>
            </div>

            <nz-card class="mx-auto max-w-3xl mb-4 md:mb-8">
              <div class="nz-card-head">
                <div class="nz-card-head-title">Policy Information</div>
                <div class="nz-card-head-description">
                  Your new insurance policy details
                </div>
              </div>
              <div class="nz-card-body">
                <div class="space-y-4">
                  <div class="grid grid-cols-2 gap-x-4 gap-y-3 md:gap-4">
                    <div>
                      <p class="text-xs md:text-sm text-gray-500">
                        Policy Number
                      </p>
                      <p class="text-sm md:text-base font-medium break-words">
                        {{ policy.policyNumber }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs md:text-sm text-gray-500">Coverage Type</p>
                      <p class="font-medium">{{ policy.product.name }}</p>
                    </div>
                    <div>
                      <p class="text-xs md:text-sm text-gray-500">Vehicle</p>
                      <p class="font-medium">
                        {{ policy.risks[0].vehicle.registrationNumber }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs md:text-sm text-gray-500">Premium</p>
                      <p class="font-medium">
                        {{ policy.grossPremium | icCurrency: policy.currency }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs md:text-sm text-gray-500">Start Date</p>
                      <p class="font-medium">
                        {{ policy.startDate | date: ICDateFormats.long }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs md:text-sm text-gray-500">End Date</p>
                      <p class="font-medium">
                        {{ policy.endDate | date: ICDateFormats.long }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </nz-card>

            <div class="grid gap-4 md:gap-6 md:grid-cols-2 mx-auto max-w-3xl">
              <nz-card>
                <div class="nz-card-head">
                  <div class="nz-card-head-title flex items-center">
                    <span
                      nz-icon
                      nzType="file-text"
                      nzTheme="outline"
                      class="mr-2 h-5 w-5"
                    ></span>
                    Policy Documents
                  </div>
                  <div class="nz-card-head-description">
                    Access your insurance documents
                  </div>
                </div>
                <div class="nz-card-body space-y-2 md:space-y-3">
                  @for (document of this.documents; track document) {
                    <div
                      class="flex items-center justify-between gap-3 p-3 border rounded-lg"
                    >
                      <span class="min-w-0 flex-1 truncate text-sm">
                        {{ document.name | sentenceCase }}
                      </span>
                      <button
                        nz-button
                        nzType="default"
                        nzSize="small"
                        [disabled]="document.isDownloading"
                        (click)="downloadDocument(document)"
                        class="flex shrink-0 items-center justify-center"
                      >
                        <span
                          nz-icon
                          nzType="download"
                          nzTheme="outline"
                          class="mr-2 h-4 w-4"
                        ></span>
                        {{ document.isDownloading ? 'Downloading...' : 'Download' }}
                      </button>
                    </div>
                  }
                </div>
              </nz-card>

              <nz-card>
                <div class="nz-card-head">
                  <div class="nz-card-head-title flex items-center">
                    Next Steps
                  </div>
                  <div class="nz-card-head-description">
                    What you can do now
                  </div>
                </div>
                <div class="nz-card-body space-y-2 md:space-y-3">
                  <div class="flex items-center gap-3 p-3 border rounded-lg">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium">Buy Another Policy</p>
                      <p class="text-xs text-gray-500">
                        Purchase additional insurance coverage
                      </p>
                    </div>
                    <button
                      (click)="toBuyAgain()"
                      nz-button
                      nzType="default"
                      class="flex shrink-0 items-center justify-center"
                    >
                      <span
                        nz-icon
                        nzType="shopping-cart"
                        nzTheme="outline"
                        class="mr-2 h-4 w-4"
                      ></span>
                      Buy Again
                    </button>
                  </div>
                  <div class="flex items-center gap-3 p-3 border rounded-lg">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium">Buy road tax</p>
                      <p class="text-xs text-gray-500">Buy road tax online</p>
                    </div>
                    <button
                      (click)="toRoadTax()"
                      nz-button
                      nzType="default"
                      class="flex shrink-0 items-center justify-center"
                    >
                      <span
                        nz-icon
                        nzType="link"
                        nzTheme="outline"
                        class="mr-2 h-4 w-4"
                      ></span>
                      Road tax
                    </button>
                  </div>
                  <div class="flex items-center gap-3 p-3 border rounded-lg">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium">Go to Dashboard</p>
                      <p class="text-xs text-gray-500">
                        Manage your policy and account
                      </p>
                    </div>
                    <button
                      nz-button
                      nzType="primary"
                      class="bg-primary flex shrink-0 items-center justify-center"
                      (click)="toDashboard()"
                    >
                      <span
                        nz-icon
                        nzType="home"
                        nzTheme="outline"
                        class="mr-2 h-4 w-4"
                      ></span>
                      Dashboard
                    </button>
                  </div>
                </div>
              </nz-card>
            </div>
          </div>
        </main>
      </div>
    } @else {
      <div class="grid grid-cols-2 lg:grid-cols-4">
        <div class="col-span-1"></div>
        <div class="col-span-2 justify-center items-center flex w-full">
          <div class="flex flex-col">
            <section class="w-full py-12">
              <div class="container px-4 md:px-6">
                <div
                  class="mx-auto flex max-w-3xl flex-col items-center justify-center space-y-6 text-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="text-green-500 size-12"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="m9 12 2 2 4-4"></path>
                  </svg>
                  <h1 class="text-3xl font-bold tracking-tighter sm:text-5xl">
                    Your insurance is active!
                  </h1>
                  <p
                    class="max-w-[700px] text-muted-foreground md:text-xl/relaxed"
                  >
                    Thank you for purchasing your insurance policy with us. You
                    can now download your policy documents.
                  </p>
                </div>
              </div>
            </section>

            @if ((loadingBarService.value$ | async) !== 0) {
              <nz-spin></nz-spin>
            } @else {
              <section class="w-full bg-muted">
                <div class="container px-4 md:px-6">
                  <div class="mx-auto max-w-3xl space-y-6">
                    <div class="grid gap-4 lg:grid-cols-3 sm:grid-cols-1">
                      @for (document of this.documents; track document) {
                        <a
                          (click)="downloadDocument(document)"
                          class="flex items-center justify-between rounded-md bg-background px-4 py-3 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <div class="flex items-center gap-4">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="h-6 w-6 text-muted-foreground"
                            >
                              <path
                                d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"
                              ></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            </svg>
                            <div>
                              <p class="font-medium">{{ document.name }}</p>
                              <p class="text-sm text-muted-foreground">
                                Download the full {{ document.name }}
                              </p>
                            </div>
                          </div>
                          @if (document.isDownloading) {
                            <nz-spin></nz-spin>
                          }
                        </a>
                      }
                    </div>
                  </div>
                </div>
              </section>
              <div class="flex my-8 justify-center space-x-3">
                <button nz-button nzType="primary" (click)="toDashboard()">
                  Home
                </button>
                <button nz-button nzType="primary" (click)="toBuyAgain()">
                  Buy Again
                </button>
                <button
                  nz-button
                  nzType="primary"
                  (click)="toRoadTax()"
                  class="flex flex-row"
                >
                  Buy Road Tax
                  <span
                    ><img
                      class="text-white"
                      src="../assets/external-link-svg.svg"
                      alt="external link"
                  /></span>
                </button>
              </div>
            }
          </div>
        </div>
        <div class="col-span-1"></div>
      </div>
    }
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentSuccessPageComponent implements OnInit, OnChanges {
  @Input() policy!: IMotorPolicy;
  @Input() useNewUi = false;
  private router = inject(Router);
  readonly policyService = inject(PoliciesService);
  readonly paymentsService = inject(PaymentsService);
  readonly documentsService = inject(DocumentService);
  readonly loadingBarService = inject(LoadingBarService);
  readonly notificationService = inject(NzNotificationService);
  protected readonly ICDateFormats = ICDateFormats;
  documents: IDocumentDownload[] = [];
  loading = false;

  toDashboard() {
    this.router.navigateByUrl('/').then();
  }

  toRoadTax() {
    window.open('https://eservices.gov.zm/#/service/4', '_blank');
  }

  toBuyAgain() {
    this.router.navigateByUrl('/').then();
  }

  ngOnInit(): void {
    if (!this.policy) {
      this.paymentsService.getPolicyRedirectedPolicy().subscribe({
        next: (value) => {
          this.policy = value as IMotorPolicy;
          this.initDocuments();
        },
        error: (err) => {
          this.notificationService.error(
            'Failed to load policy',
            err?.error?.message ??
              'Unable to retrieve your policy. Please go to your dashboard.',
          );
          this.router.navigateByUrl('/dashboard').then();
        },
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['policy'] && this.policy) {
      this.initDocuments();
    }
  }

  createDocumentUrl(id: string | number, path: string): string {
    if (this.policy.product.classOfBusiness.name === 'Motor') {
      return `${baseUrl()}/policies/${id}/motor/documents/${path}`;
    }

    if (this.policy.product.classOfBusiness.name === 'Travel') {
      return `${baseUrl()}/travel/${id}/documents/${path}`;
    }

    if (this.policy.product.classOfBusiness.name === 'Life') {
      return `${baseUrl()}/life/policies/${id}/documents/${path}`;
    }

    return `${baseUrl()}/policies/general/${id}/documents/${path}`;
  }

  downloadDocument(doc: IDocumentDownload) {
    if (doc.openInNewWindow) {
      window.open(doc.url, '_blank');
    } else {
      this.updateDocLoadingState(doc, true);
      this.documentsService.downloadDocument(doc.url, doc.name).subscribe({
        next: () => {
          this.updateDocLoadingState(doc, false);
        },
        error: (err) => {
          this.updateDocLoadingState(doc, false);
          this.notificationService.error(
            'Download failed',
            err?.error?.message ??
              `Could not download "${doc.name}". Please try again.`,
          );
        },
      });
    }
  }

  initDocuments() {
    if (!this.policy) return;
    if (isPiczInsurer()) {
      //handle motor
      if (this.policy.product.classOfBusiness.name === 'Motor') {
        this.loading = true;

        this.policyService.getPiczDocuments(this.policy.id).subscribe({
          next: (value) => {
            this.documents = [...value];
            this.loading = false;
          },
          error: (err) => {
            this.loading = false;
            this.notificationService.error(
              'Failed to load documents',
              err?.error?.message ??
                'Could not retrieve policy documents. Please try again.',
            );
          },
        });
      }
    } else {
      //defaults
      this.documents = [
        ...this.policy.product.documents.map((doc) => ({
          name: doc.name,
          url: doc.url,
          isDownloading: false,
          openInNewWindow: false,
        })),
      ];
      if (this.policy.status !== 'Draft') {
        this.documents = [
          {
            name: 'Certificate Of Insurance',
            url: this.createDocumentUrl(
              this.policy.id,
              'certificate',
            ),
            isDownloading: false,
            openInNewWindow: false,
          },
          {
            name: 'Receipt',
            url: this.createDocumentUrl(this.policy.id, 'receipt'),
            isDownloading: false,
            openInNewWindow: false,
          },
          {
            name: 'Policy schedule',
            url: this.createDocumentUrl(this.policy.id, 'schedule'),
            isDownloading: false,
            openInNewWindow: false,
          },
          ...this.documents,
        ];
      }
    }
  }

  updateDocLoadingState(doc: IDocumentDownload, isLoading: boolean) {
    this.documents = [...this.documents.map((d) => {
      if (d.name === doc.name) {
        return { ...d, isDownloading: isLoading };
      }
      return d;
    })];
  }
}
